import { useEffect, useState } from 'react';
import { audioEngine } from './engine/audio';
import { loadModule } from './engine/loaders';
import { useTracker } from './state/store';
import { useTrackerKeyboard } from './ui/hooks/useTrackerKeyboard';
import { PatternEditor } from './ui/components/PatternEditor';
import { TransportBar } from './ui/components/TransportBar';
import { OrderList } from './ui/components/OrderList';
import { InstrumentList } from './ui/components/InstrumentList';
import { SampleEditor } from './ui/components/SampleEditor';
import { EnvelopeEditor } from './ui/components/EnvelopeEditor';
import { SettingsPanel } from './ui/components/SettingsPanel';
import { SongPanel } from './ui/components/SongPanel';
import { ModArchive } from './ui/components/ModArchive';
import { Scopes } from './ui/components/Scopes';
import { HelpOverlay } from './ui/components/HelpOverlay';
import { Button } from './ui/components/primitives';

type SidePanel = 'song' | 'instrument' | 'sound' | 'archive';

export default function App() {
  const [ready, setReady] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [panel, setPanel] = useState<SidePanel>('instrument');
  const [dropping, setDropping] = useState(false);

  const song = useTracker((s) => s.song);
  const setPlayback = useTracker((s) => s.setPlayback);
  const loadSong = useTracker((s) => s.loadSong);
  const dirty = useTracker((s) => s.dirty);

  useTrackerKeyboard(ready);

  // Push the worklet's transport snapshots into the store.
  useEffect(() => {
    return audioEngine.onSnapshot((snapshot) => {
      // Follow means following the arrangement, not just the row: once playback
      // moves into the next order position the editor has to move with it or
      // the play cursor vanishes into a pattern nobody is looking at.
      const store = useTracker.getState();
      if (
        store.followPlayback &&
        snapshot.state.playing &&
        snapshot.state.order !== store.currentOrder
      ) {
        store.setCurrentOrder(snapshot.state.order);
      }

      setPlayback({
        playing: snapshot.state.playing,
        order: snapshot.state.order,
        pattern: snapshot.state.pattern,
        row: snapshot.state.row,
        speed: snapshot.state.speed,
        tempo: snapshot.state.tempo,
        peaks: snapshot.peaks,
        scopes: snapshot.scopes,
        scopeLength: snapshot.scopeLength,
        masterPeak: snapshot.masterPeak,
      });
    });
  }, [setPlayback]);

  // Warn before losing unsaved work.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const start = async () => {
    try {
      await audioEngine.init();
      audioEngine.loadSong(useTracker.getState().song);
      audioEngine.setMixerSettings(useTracker.getState().mixerSettings);
      setReady(true);
    } catch (error) {
      setAudioError(error instanceof Error ? error.message : 'Could not start audio.');
    }
  };

  const onDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setDropping(false);
    const file = event.dataTransfer.files[0];
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      const { song: loaded, warnings } = loadModule(buffer);
      await audioEngine.init();
      setReady(true);
      loadSong(loaded, file.name, warnings);
    } catch (error) {
      setAudioError(error instanceof Error ? error.message : 'Could not read that file.');
      setTimeout(() => setAudioError(null), 5000);
    }
  };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-950 p-6">
        <div className="max-w-lg text-center">
          <h1 className="font-sans text-4xl font-bold tracking-tight text-white">
            Fin<span className="text-acid-500">Tracker</span>
          </h1>
          <p className="mt-3 font-mono text-xs leading-relaxed text-ink-400">
            A ProTracker and FastTracker&nbsp;II compatible tracker for the browser. Plays MOD and XM
            with Amiga Paula emulation, and writes them too.
          </p>
          <div className="mt-6">
            <Button onClick={start} tone="accent" className="px-6 py-2 text-sm">
              Start audio
            </Button>
          </div>
          <p className="mt-4 font-mono text-[10px] text-ink-500">
            Browsers only allow audio after a click. Drop a .mod or .xm anywhere once you are in.
          </p>
          {audioError && (
            <p className="mt-4 rounded border border-red-900 bg-red-950 px-3 py-2 font-mono text-[11px] text-red-300">
              {audioError}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-ink-950 text-white"
      onDragOver={(e) => {
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={onDrop}
    >
      <TransportBar />

      <div className="flex min-h-0 flex-1 gap-2 p-2">
        <aside className="flex w-[112px] shrink-0 flex-col">
          <OrderList />
        </aside>

        <main className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-ink-700 bg-ink-950 shadow-panel">
          <PatternEditor />
        </main>

        <aside className="flex w-[340px] shrink-0 flex-col gap-2 overflow-hidden">
          <div className="flex shrink-0 gap-1">
            {(
              [
                ['instrument', 'Instr'],
                ['song', 'Song'],
                ['sound', 'Sound'],
                ['archive', 'Archive'],
              ] as [SidePanel, string][]
            ).map(([key, label]) => (
              <Button
                key={key}
                active={panel === key}
                onClick={() => setPanel(key)}
                className="flex-1"
              >
                {label}
              </Button>
            ))}
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            {panel === 'instrument' && (
              <>
                <div className="h-[200px] shrink-0">
                  <InstrumentList />
                </div>
                <SampleEditor />
                <EnvelopeEditor />
              </>
            )}
            {panel === 'song' && <SongPanel />}
            {panel === 'sound' && <SettingsPanel />}
            {panel === 'archive' && <ModArchive />}
          </div>
        </aside>
      </div>

      <Scopes />

      <footer className="flex shrink-0 items-center gap-3 border-t border-ink-700 bg-ink-900 px-3 py-1">
        <Button onClick={() => setShowHelp(true)}>? Keys</Button>
        <span className="font-mono text-[10px] text-ink-500">
          {song.channels} channels · {song.format.toUpperCase()} ·{' '}
          {song.mode === 'pt' ? 'ProTracker' : 'FastTracker II'} replay
        </span>
        <span className="ml-auto font-mono text-[10px] text-ink-600">
          Space edits · Enter plays · ? for keys
        </span>
      </footer>

      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}

      {dropping && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-acid-500/10 backdrop-blur-sm">
          <div className="rounded-xl border-2 border-dashed border-acid-500 px-8 py-6 font-mono text-sm text-acid-300">
            Drop a .mod or .xm to load it
          </div>
        </div>
      )}

      {audioError && (
        <div className="fixed bottom-16 left-1/2 z-50 -translate-x-1/2 rounded border border-red-900 bg-red-950 px-4 py-2 font-mono text-[11px] text-red-300">
          {audioError}
        </div>
      )}
    </div>
  );
}
